import * as Cesium from 'cesium';

// Rain shader adapted from Élie Michel's rain effect
// Original License: CC BY 3.0 - July 2017
// Converted from Shadertoy to Cesium PostProcessStage

const rainShaderSource = `
uniform sampler2D colorTexture;
uniform float u_time;
uniform float u_intensity;
uniform float u_speed;
uniform float u_blur;

in vec2 v_textureCoordinates;
out vec4 fragColor;

vec2 rand(vec2 c){
    mat2 m = mat2(12.9898, 0.16180, 78.233, 0.31415);
    return fract(sin(m * c) * vec2(43758.5453, 14142.1));
}

vec2 noise(vec2 p){
    vec2 co = floor(p);
    vec2 mu = fract(p);
    mu = 3.0 * mu * mu - 2.0 * mu * mu * mu;
    vec2 a = rand((co + vec2(0.0, 0.0)));
    vec2 b = rand((co + vec2(1.0, 0.0)));
    vec2 c = rand((co + vec2(0.0, 1.0)));
    vec2 d = rand((co + vec2(1.0, 1.0)));
    return mix(mix(a, b, mu.x), mix(c, d, mu.x), mu.y);
}

void main()
{
    vec2 resolution = czm_viewport.zw;
    vec2 fragCoord = v_textureCoordinates * resolution;

    vec2 u = v_textureCoordinates;
    vec2 v = (fragCoord * 0.1) / resolution;
    vec2 n = noise(v * 200.0); // Displacement

    // Sample with slight blur for base image
    vec4 color = texture(colorTexture, u);

    // Loop through the different inverse sizes of drops
    for (float r = 4.0; r > 0.0; r -= 1.0) {
        vec2 x = resolution * r * 0.015 * u_intensity;  // Number of potential drops (in a grid)
        vec2 p = 6.28 * u * x + (n - 0.5) * 2.0;
        vec2 s = sin(p);

        // Current drop properties. Coordinates are rounded to ensure a
        // consistent value among the fragment of a given drop.
        vec2 gridPos = round(u * x - 0.25) / x;
        vec4 d = vec4(noise(gridPos * 200.0), noise(gridPos));

        // Drop shape and fading
        float animTime = u_time * u_speed;
        float t = (s.x + s.y) * max(0.0, 1.0 - fract(animTime * (d.b + 0.1) + d.g) * 2.0);

        // d.r -> only x% of drops are kept on, with x depending on the size of drops
        if (d.r < (5.0 - r) * 0.08 && t > 0.5) {
            // Drop normal
            vec3 normal = normalize(-vec3(cos(p), mix(0.2, 2.0, t - 0.5)));

            // Refraction through water droplet
            color = texture(colorTexture, u - normal.xy * 0.3 * u_blur);
        }
    }

    fragColor = color;
}
`;

/**
 * Initialize the rain post-processing effect
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Object} params - Effect parameters
 * @param {number} params.intensity - Drop density (0-1, default 1.0)
 * @param {number} params.speed - Animation speed (default 1.0)
 * @param {number} params.blur - Refraction strength (0-1, default 1.0)
 * @returns {Object} { stage, uniforms } - The PostProcessStage and its uniforms object
 */
export function initializeRainEffect(viewer, params = {}) {
    const {
        intensity = 1.0,
        speed = 1.0,
        blur = 1.0
    } = params;

    // Create uniforms object
    const uniforms = {
        u_time: 0.0,
        u_intensity: intensity,
        u_speed: speed,
        u_blur: blur
    };

    // Create the PostProcessStage
    const rainStage = new Cesium.PostProcessStage({
        name: 'rain_effect',
        fragmentShader: rainShaderSource,
        uniforms: uniforms
    });

    // Add to viewer's post-process stages
    viewer.scene.postProcessStages.add(rainStage);

    // Start animation loop to update time uniform
    let lastTime = performance.now();
    const animationCallback = () => {
        const currentTime = performance.now();
        const deltaTime = (currentTime - lastTime) / 1000.0; // Convert to seconds
        rainStage.uniforms.u_time += deltaTime;
        lastTime = currentTime;
    };

    // Store animation callback for cleanup
    rainStage._animationCallback = animationCallback;
    viewer.clock.onTick.addEventListener(animationCallback);

    return { stage: rainStage, uniforms: rainStage.uniforms };
}

/**
 * Update rain effect parameters
 * @param {Object} uniforms - The uniforms object returned from initializeRainEffect
 * @param {Object} params - New parameter values
 */
export function updateRainEffect(uniforms, params) {
    if (params.intensity !== undefined) {
        uniforms.u_intensity = params.intensity;
    }
    if (params.speed !== undefined) {
        uniforms.u_speed = params.speed;
    }
    if (params.blur !== undefined) {
        uniforms.u_blur = params.blur;
    }
}

/**
 * Clean up the rain effect
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Cesium.PostProcessStage} stage - The rain effect stage to remove
 */
export function cleanupRainEffect(viewer, stage) {
    if (stage && stage._animationCallback) {
        viewer.clock.onTick.removeEventListener(stage._animationCallback);
        delete stage._animationCallback;
    }
    if (stage) {
        viewer.scene.postProcessStages.remove(stage);
    }
}
